import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import colors from '../../helpers/colors';
import Icon from '../Icon';

/*  This method will return a reference object and a react node to render the custom bullet
    points for a block of html text. The reference object can be passed to a HTML div
    element, and the html will have a <span> element with class "bullet" inserted at the
    beginning of each <li> element.

    The customBullets node renders the custom bullet icon into each "bullet" span
    using React Portals: https://reactjs.org/docs/portals.html
*/
const useCustomBullets = (
  ref: React.RefObject<HTMLDivElement>,
  html: string,
  customBulletIcon: string | undefined
): ReactNode => {
  const [iconSpans, setIconSpans] = React.useState<HTMLSpanElement[]>([]);

  const insertBulletSpanIntoElement = (element: HTMLElement): HTMLSpanElement => {
    const span = document.createElement('span');
    span.classList.add('bullet');
    element.insertAdjacentElement('afterbegin', span);
    return span;
  };

  React.useEffect(() => {
    if (ref.current) {
      const liListItems = Array.from(
        ref.current.querySelectorAll<HTMLLIElement>('ul > li')
      );

      const spans = liListItems.map((li) => {
        /*  This check is necessary to avoid extra spans being added for each <li> in Storybook
            if we toggle between icons and bullet points.  If a bullet span already exists, we
            return it rather than inserting a new one.
        */
        const existingSpan = li.querySelector<HTMLSpanElement>('span.bullet');
        return existingSpan ?? insertBulletSpanIntoElement(li);
      });

      setIconSpans(spans);
    }
  }, [ref, html]);

  const customBullets = customBulletIcon
    ? iconSpans.map((span) =>
        createPortal(
          <Icon name={customBulletIcon} size="small" color={colors.core01} />,
          span
        )
      )
    : null;

  return customBullets;
};

export default useCustomBullets;
